import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import MfaDevice from 'services/data/mfa-device';
import MfaConnectionLog from 'services/data/mfa-connection-log';
import Person from 'services/data/person';
import { useSocket } from 'services/socket';

export function useMfaDevices(queryOptions = {}) {
  const key = ['mfadevice', { status: 'Active' }];

  const queryClient = useQueryClient();
  const queryData = useQuery(
    key,
    () => MfaDevice.find({ ...MfaDevice.defaultQuery, where: { status: 'Active' } }),
    queryOptions
  );

  const refetch = useCallback(() => queryClient.refetchQueries(key), [queryClient]);

  return { ...queryData, refetch };
}

export function useActiveMfaConnections(queryOptions = {}) {
  const key = ['mfaconnectionlog', { active: true }];

  const queryClient = useQueryClient();
  const queryData = useQuery(
    key,
    () => MfaConnectionLog.find({ where: { disconnectedAt: null }, load: ['person'] }),
    queryOptions
  );

  const refetch = useCallback(() => queryClient.refetchQueries(key), [queryClient]);
  useSocket('mfadevice/status', refetch, refetch);

  return { ...queryData, refetch };
}

export function useMfaDevicesWithConnections(queryOptions = {}) {
  const { data: devices = [], ...deviceQuery } = useMfaDevices(queryOptions);
  const { data: activeLogs = [] } = useActiveMfaConnections();

  const connectedUsersByDevice = useMemo(() => {
    const byDevice = new Map();
    activeLogs.forEach((log) => {
      const list = byDevice.get(log.mfaDevice) || [];
      list.push(Person.fullName(log.person));
      byDevice.set(log.mfaDevice, list);
    });
    return byDevice;
  }, [activeLogs]);

  const data = useMemo(() => devices.map((device) => ({
    ...device,
    connectedUsers: connectedUsersByDevice.get(device.id) || []
  })), [devices, connectedUsersByDevice]);

  return { ...deviceQuery, data };
}
